import AddProductToAccountModal from './AddProductToAccountModal';
import AllocationDialog from './AllocationDialog';
import AllocationInstructionsEmailDialog from './AllocationInstructionsEmailDialog';
import BulkAllocationDialog from './BulkAllocationDialog';
import DataGrid from '@components/common/DataGrid';
import EmptyState from '@components/common/EmptyState';
import EntitlementActionsMenu from '@components/organizations/entitlements/EntitlementActionsMenu';
import EntitlementQuotaBar from '@components/common/quota/EntitlementQuotaBar';
import ErrorPage from '@account/components/common/ErrorPage';
import Header from '@components/common/layout/Header';
import Notifier from '@components/common/Notifier';
import PropTypes from 'prop-types';
import QuotaAdjustModal from '@components/organizations/entitlements/QuotaAdjustModal';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import RecallWorkspacesModal from '@components/products/RecallWorkspacesModal';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import routePath, {Routes} from '@account/utils/route';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  Link,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  EntitlementSource,
  EntitlementStatus,
  buildItemTypeNameMap,
  getEntitlementPrimaryQuota,
  normalizeStatus,
  statusColorMap,
} from '@account/utils/entitlements';
import {
  PA_BULK_LIMIT,
  deactivateProductAccess as _deactivateProductAccess,
  getProductAccesses,
  getWorkspacesForCA,
} from '@account/api/products';
import {
  Link as RouterLink,
  useHistory,
  useLocation,
  useParams,
} from 'react-router-dom';
import {displayDate} from '@account/utils/dates';
import {getError} from '@account/utils/errors';
import {hasRoleLevel, roleLevels} from '@account/utils';
import {programName} from '@account/utils/programs';
import {renderQuota} from '@account/utils/quota';
import {useAuth} from '@apps-common/auth-react';
import {useCAEntitlements} from '@hooks/useEntitlements';
import {useCustomerAccount} from '@hooks/data/ims/useCustomerAccount';
import {useFlags} from '@hooks/useFlags';
import {useGetItemTypes} from '@account/api/itemTypes';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';

import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import SearchIcon from '@mui/icons-material/Search';

const INSIGHTS_WORKSPACE_URL =
  'https://insights.planet.com/account/#/workspaces';

const CopyableId = ({label, value, display}) => {
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef(null);
  const truncated =
    value && value.length > 12
      ? `${value.slice(0, 8)}...${value.slice(-4)}`
      : value;

  useEffect(() => () => clearTimeout(resetTimer.current), []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // Clipboard denied or unavailable (e.g. non-secure context) — don't show
      // a false "Copied!"; leave the affordance in its default state.
      return;
    }
    setCopied(true);
    clearTimeout(resetTimer.current);
    resetTimer.current = setTimeout(() => setCopied(false), 1500);
  };

  // Nothing to copy without a value — render nothing rather than a chip that
  // would copy/show the literal "undefined".
  if (!value) return null;

  return (
    <Tooltip title={copied ? 'Copied!' : `${label}: ${value} (click to copy)`}>
      <Chip
        label={`${label}: ${display ?? truncated}`}
        size="small"
        variant="outlined"
        onClick={handleCopy}
        sx={{
          cursor: 'pointer',
          fontSize: '0.7rem',
          height: 24,
          '& .MuiChip-label': {px: 1},
        }}
      />
    </Tooltip>
  );
};

CopyableId.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  // Optional human-readable label shown in the chip in place of the (possibly
  // truncated) raw value; `value` is still what gets copied and shown on hover.
  display: PropTypes.string,
};

// Icon-only copy affordance for places too tight for a full CopyableId chip
// (e.g. the workspace card header). Same one-click clipboard + "Copied!"
// feedback; stops propagation so it doesn't toggle a surrounding accordion.
const CopyIconButton = ({label, value}) => {
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef(null);

  useEffect(() => () => clearTimeout(resetTimer.current), []);

  const handleCopy = async e => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // Clipboard denied or unavailable (e.g. non-secure context) — don't show
      // a false "Copied!"; leave the affordance in its default state.
      return;
    }
    setCopied(true);
    clearTimeout(resetTimer.current);
    resetTimer.current = setTimeout(() => setCopied(false), 1500);
  };

  // Nothing to copy without a value — render nothing rather than an icon that
  // would copy the literal "undefined".
  if (!value) return null;

  return (
    <Tooltip title={copied ? 'Copied!' : `${label}: ${value} (click to copy)`}>
      <IconButton
        size="small"
        aria-label={`Copy ${label}`}
        onClick={handleCopy}
      >
        {copied ? (
          <CheckIcon fontSize="small" />
        ) : (
          <ContentCopyIcon fontSize="small" />
        )}
      </IconButton>
    </Tooltip>
  );
};

CopyIconButton.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
};

const MAX_PRODUCT_CHIPS = 2;

// The "Products" column: item types for a plan (PSScene, SkySat…), catalog SKU
// names for a PA. `nameMap` hydrates raw item-type ids to display names; a miss
// falls back to the raw id, and an empty list renders "—" — so the cell degrades
// cleanly before the backend exposes plan.meta or if the catalog fails to load.
const ProductsCell = ({products, nameMap}) => {
  if (!products?.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  const shown = products.slice(0, MAX_PRODUCT_CHIPS);
  const extra = products.length - shown.length;
  return (
    <Tooltip title={products.map(p => nameMap[p] || p).join(', ')}>
      <Stack direction="row" spacing={0.5} alignItems="center">
        {shown.map(p => (
          <Chip
            key={p}
            label={nameMap[p] || p}
            size="small"
            variant="outlined"
            sx={{height: 20, fontSize: '0.65rem', maxWidth: 130}}
          />
        ))}
        {extra > 0 && (
          <Typography variant="caption" color="text.secondary">
            +{extra}
          </Typography>
        )}
      </Stack>
    </Tooltip>
  );
};

ProductsCell.propTypes = {
  products: PropTypes.arrayOf(PropTypes.string),
  nameMap: PropTypes.object,
};

const CustomNoRowsOverlay = () => (
  <EmptyState
    icon={TravelExploreIcon}
    text="No entitlements for this account"
  />
);

const formatDate = (iso, fallback = '') => {
  if (!iso) return fallback;
  return displayDate(iso);
};

// The PA's term_end_date is the end of the latest term that hasn't ended yet
// (active or pending). The backend returns null once every term has lapsed, so
// a null date means "no live term" - an expired PA, NOT a perpetual one. We
// don't yet surface the real historical end date (backend service_end_date
// isn't exposed on the read schema), so show a neutral placeholder rather than
// the misleading "Ongoing". Interim fix for CGO-3173 follow-up.
const termEndsLabel = row => (row?.endDate ? formatDate(row.endDate) : '—');
const termEndsTooltip = row =>
  row?.endDate
    ? `${formatDate(row.startDate)} — ${formatDate(row.endDate)}`
    : 'No active term';

// Status filter for the CA product list and the Workspaces PA list. Single-
// select, matching the Access Periods table on the product detail page. Each
// chip maps to the backend `state` array. Default is 'active'.
const STATUS_FILTERS = [
  {label: 'All', value: 'all'},
  {label: 'Active', value: 'active'},
  {label: 'Pending', value: 'pending'},
  {label: 'Expired', value: 'expired'},
];
const STATUS_FILTER_STATES = {
  all: ['active', 'pending', 'inactive', 'deactivated', 'expired'],
  active: ['active'],
  pending: ['pending'],
  expired: ['expired', 'inactive', 'deactivated'],
};

// Chip value -> EntitlementStatus[] for the CA Products list. That list is the
// unified client-side entitlements list (CA PAs + legacy plans), so the chips
// filter it by EntitlementStatus client-side (via useCAEntitlements'
// statusFilter). `all` = no filter. Mirrors STATUS_FILTER_STATES, which is the
// server-side equivalent used by the Workspaces tab.
const STATUS_FILTER_ENTITLEMENTS = {
  all: undefined,
  active: [EntitlementStatus.ACTIVE],
  pending: [EntitlementStatus.PENDING],
  expired: [EntitlementStatus.EXPIRED, EntitlementStatus.INACTIVE],
};

// The Products tab filters PRODUCTS by rolled-up status; the product detail
// page's identical-looking chips filter individual TERMS. To keep the two from
// reading as the same control, the Products tab drops "Pending": a wholly
// not-yet-started PA is vanishingly rare (~54 of 750k PAs in prod), and the
// chip invited the exact confusion of "I filtered Pending but my upcoming term
// didn't show" - because a PA with an upcoming term is Active, not Pending.
// Wholly-pending PAs still surface under "All". CGO-3173 follow-up.
const PRODUCT_STATUS_FILTERS = STATUS_FILTERS.filter(
  f => f.value !== 'pending'
);

// Map a raw product-access state to a display status + MUI chip color, reusing
// the shared entitlement mapping so the Workspaces tab matches the Products tab.
const PA_STATUS_LABELS = {
  [EntitlementStatus.ACTIVE]: 'Active',
  [EntitlementStatus.PENDING]: 'Pending',
  [EntitlementStatus.EXPIRED]: 'Expired',
  [EntitlementStatus.INACTIVE]: 'Inactive',
  [EntitlementStatus.ERROR]: 'Error',
};
const paStatus = state => {
  const status = normalizeStatus(state);
  return {
    label: PA_STATUS_LABELS[status] ?? status,
    color: statusColorMap[status],
  };
};

// A workspace PA counts as "active" (for the workspace header cue and row
// emphasis) when it is active or pending - anything an admin still treats as a
// live product.
const isActivePa = pa =>
  ['active', 'pending'].includes((pa.state || '').toLowerCase());

const groupByWorkspace = (imsWorkspaces, workspacePas) => {
  const pasByWsId = {};
  for (const pa of workspacePas ?? []) {
    if (!pa.workspace_id) continue;
    const key = String(pa.workspace_id);
    if (!pasByWsId[key]) pasByWsId[key] = [];
    pasByWsId[key].push(pa);
  }
  return (imsWorkspaces ?? [])
    .map(ws => {
      const pas = pasByWsId[ws.workspace_id] ?? [];
      const quotaByUnit = {};
      for (const pa of pas) {
        const unit = pa.quota_units || '_none';
        if (!quotaByUnit[unit]) quotaByUnit[unit] = {total: 0, used: 0};
        quotaByUnit[unit].total += pa.quota_total || 0;
        quotaByUnit[unit].used += pa.quota_used || 0;
      }
      return {
        workspaceId: ws.workspace_id,
        workspaceName: ws.name || 'Unnamed Workspace',
        isDefault: ws.isDefault,
        pas,
        quotaByUnit,
      };
    })
    .sort((a, b) => b.pas.length - a.pas.length);
};

// Row-click destination for an entitlement row, or null if the row isn't
// navigable. PLAN rows open the read-only plan detail; PRODUCT_ACCESS rows open
// product detail. CONTRACT / TASKING_CONTRACT rows are not navigable here.
const entitlementRoute = (row, accountId) => {
  if (row.source === EntitlementSource.PLAN) {
    return routePath(Routes.ACCOUNT_PLAN_DETAIL, {
      accountId,
      planId: row.sourceId,
    });
  }
  if (row.source === EntitlementSource.PRODUCT_ACCESS) {
    return routePath(Routes.PRODUCT_DETAIL, {productAccessId: row.sourceId});
  }
  return null;
};

const NewAccountDetailPage = () => {
  const {accountId} = useParams();
  const history = useHistory();
  const location = useLocation();
  const auth = useAuth();
  const theme = useTheme();
  const isPlanetAdmin = hasRoleLevel(auth, [roleLevels.planet_admin]);
  const {showAllocationUI} = useFlags();
  const queryClient = useQueryClient();

  const urlParams = new URLSearchParams(location.search);
  const [tabIndex, setTabIndex] = useState(0);
  // Status filter for the Products tab. Single-select, matching the Access
  // Periods table on the product detail page. Default 'active'. A stale URL can
  // carry ?productStatus=pending (a value the Products tab no longer offers as a
  // chip), which would filter the list with no chip lit and no obvious way to
  // clear it - normalize any unoffered value back to 'active'.
  const rawProductStatus = urlParams.get('productStatus') || 'active';
  const productStatus = PRODUCT_STATUS_FILTERS.some(
    f => f.value === rawProductStatus
  )
    ? rawProductStatus
    : 'active';
  const setProductStatus = value => {
    const p = new URLSearchParams(location.search);
    if (value && value !== 'active') p.set('productStatus', value);
    else p.delete('productStatus');
    history.replace({search: p.toString()});
  };
  // Independent status filter for the Workspaces tab's product-access list.
  const wsStatus = urlParams.get('wsStatus') || 'active';
  const setWsStatus = value => {
    const p = new URLSearchParams(location.search);
    if (value && value !== 'active') p.set('wsStatus', value);
    else p.delete('wsStatus');
    history.replace({search: p.toString()});
  };
  const [productSearch, setProductSearch] = useState('');
  const [modalMode, setModalMode] = useState(null);
  const [adjustTarget, setAdjustTarget] = useState(null);
  const [recallTarget, setRecallTarget] = useState(null);
  const [wsAllocTarget, setWsAllocTarget] = useState(null);
  const [allocateTarget, setAllocateTarget] = useState(null);
  const [bulkAllocateOpen, setBulkAllocateOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [deactivateError, setDeactivateError] = useState(null);
  const [productFilter, setProductFilter] = useState(null);

  // The SFDC provisioning flow routes here on success with
  // ?postProvision=allocation-email to prompt the CSM to email the customer.
  // Open the dialog once, then strip the param so a refresh won't reopen it.
  useEffect(() => {
    if (urlParams.get('postProvision') === 'allocation-email') {
      setEmailDialogOpen(true);
      const p = new URLSearchParams(location.search);
      p.delete('postProvision');
      history.replace({search: p.toString()});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const accountQuery = useCustomerAccount(accountId, {
    keepPreviousData: true,
    enabled: isPlanetAdmin && !!accountId,
  });

  const account = accountQuery.data ?? {};

  // Unified product-entitlement list for the CA: CA-level Product Accesses plus
  // legacy subscriptions rolled up across the CA's workspaces/orgs (read-only).
  // Fully fetched and filtered/paginated client-side so both sources sit in one
  // grid; the status chips filter it by EntitlementStatus.
  const statusFilter = useMemo(
    () => STATUS_FILTER_ENTITLEMENTS[productStatus] ?? undefined,
    [productStatus]
  );
  const {
    data: entitlements = [],
    isLoading: entLoading,
    isError: entError,
  } = useCAEntitlements(accountId, {
    statusFilter,
    enabled: isPlanetAdmin && !!accountId,
    keepPreviousData: true,
  });

  // Raw CA-level product accesses — the source pool for bulk allocation. Only
  // active/pending PAs are eligible to allocate quota from. Plans are excluded
  // (source filter) since they are read-only and not allocatable.
  const caProductAccesses = useMemo(
    () =>
      entitlements
        .filter(
          e =>
            e.source === EntitlementSource.PRODUCT_ACCESS &&
            (e.status === EntitlementStatus.ACTIVE ||
              e.status === EntitlementStatus.PENDING)
        )
        .map(e => e.rawPayload),
    [entitlements]
  );

  const activeCount = useMemo(
    () =>
      entitlements.filter(e => e.status === EntitlementStatus.ACTIVE).length,
    [entitlements]
  );

  // Item-type catalog (same source web-admin-ng uses) to hydrate raw item-type
  // ids to display names in the Products column. Best-effort: a failure leaves
  // the map empty and the column falls back to raw ids.
  const {data: itemTypesCatalog} = useGetItemTypes();
  const itemTypeNameMap = useMemo(
    () => buildItemTypeNameMap(itemTypesCatalog),
    [itemTypesCatalog]
  );

  // Client-side product search over the fully-fetched list. Memoized so the
  // grid's `rows` keeps a stable identity between unrelated re-renders.
  const visibleRows = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    if (!q) return entitlements;
    return entitlements.filter(
      e =>
        e.name?.toLowerCase().includes(q) ||
        e.displayId?.toLowerCase().includes(q) ||
        (e.plNumbers ?? []).some(pl => pl.toLowerCase().includes(q))
    );
  }, [entitlements, productSearch]);

  const workspacePasQuery = useQuery({
    queryKey: ['caWorkspacePas', accountId, wsStatus],
    queryFn: () =>
      getProductAccesses({
        customerAccountId: accountId,
        limit: PA_BULK_LIMIT,
        // Mirror the Products tab's status filter so admins can surface expired
        // (or pending) workspace allocations when hunting for a specific PA.
        state: STATUS_FILTER_STATES[wsStatus] ?? STATUS_FILTER_STATES.active,
      }),
    enabled: !!accountId && (tabIndex === 1 || !!recallTarget),
  });
  const workspacePas = useMemo(
    () =>
      (workspacePasQuery.data?.results || []).filter(pa => !!pa.workspace_id),
    [workspacePasQuery.data]
  );
  const workspacePasTruncated =
    (workspacePasQuery.data?.meta?.count ?? 0) > PA_BULK_LIMIT;

  const {
    data: imsWorkspacesData,
    isInitialLoading: imsWorkspacesLoading,
    isError: imsWorkspacesError,
  } = useQuery({
    queryKey: ['workspaces-for-ca', accountId],
    queryFn: () => getWorkspacesForCA(accountId),
    enabled: !!accountId,
  });
  const imsWorkspaces = imsWorkspacesData?.workspaces;
  const workspaceTruncated = imsWorkspacesData?.truncated ?? false;

  // Gate on both queries: workspace metadata AND the product-access query that
  // supplies the per-workspace product rows. isInitialLoading (not isLoading)
  // keeps refetches from re-flashing the skeleton, and — unlike checking
  // `!imsWorkspaces` — resolves to false on error so we fall through to the
  // error state instead of a stuck skeleton.
  const workspacesLoading =
    imsWorkspacesLoading || workspacePasQuery.isInitialLoading;
  // Surface load failures explicitly — otherwise a failed product-access query
  // renders every workspace as having zero products (wrong admin data).
  const workspacesError = imsWorkspacesError || workspacePasQuery.isError;

  const workspaces = useMemo(
    () => groupByWorkspace(imsWorkspaces, workspacePas || []),
    [imsWorkspaces, workspacePas]
  );

  const orphanedPaWorkspaces = useMemo(() => {
    if (!imsWorkspaces || !workspacePas) return [];
    const imsIds = new Set(imsWorkspaces.map(ws => ws.workspace_id));
    const orphaned = new Set();
    for (const pa of workspacePas) {
      if (pa.workspace_id && !imsIds.has(pa.workspace_id)) {
        orphaned.add(pa.workspace_id);
      }
    }
    return Array.from(orphaned);
  }, [imsWorkspaces, workspacePas]);

  const sfdcAccountUrl = account.sfdc_id
    ? `https://planetlabs.lightning.force.com/lightning/r/Account/${account.sfdc_id}/view`
    : null;

  const breadcrumbs = useMemo(
    () => [
      {url: Routes.ACCOUNTS, label: 'Customer Accounts'},
      {label: account?.name ?? '...'},
    ],
    [account?.name]
  );

  const handleAdjustQuota = useCallback(ent => setAdjustTarget(ent), []);
  const handleAllocate = useCallback(
    ent => setAllocateTarget(ent.rawPayload),
    []
  );
  const handleRecallQuota = useCallback(ent => setRecallTarget(ent), []);
  const handleFilterWorkspaces = useCallback(ent => {
    setProductFilter(ent.rawPayload?.edp_id ?? null);
    setTabIndex(1);
  }, []);
  const handleDeactivate = useCallback(ent => setDeactivateTarget(ent), []);

  const {mutate: doDeactivate, isLoading: isDeactivating} = useMutation(
    () => _deactivateProductAccess(deactivateTarget?.sourceId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['productAccesses']);
        setDeactivateTarget(null);
        setDeactivateError(null);
      },
      onError: err => {
        setDeactivateError(
          getError(err)?.message ?? 'Deactivation failed — please try again.'
        );
      },
    }
  );

  const entitlementColumns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Product',
        minWidth: 220,
        flex: 0.4,
        renderCell: ({row}) => (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" fontWeight={600}>
              {row.name}
            </Typography>
            {row.source === EntitlementSource.PLAN && (
              <Tooltip title="Legacy plan — read-only here">
                <Chip
                  label="Plan"
                  size="small"
                  variant="outlined"
                  sx={{height: 20, fontSize: '0.65rem'}}
                />
              </Tooltip>
            )}
          </Stack>
        ),
      },
      {
        field: 'products',
        headerName: 'Products',
        flex: 0.35,
        minWidth: 160,
        sortable: false,
        renderCell: ({row}) => (
          <ProductsCell products={row.products} nameMap={itemTypeNameMap} />
        ),
      },
      {
        field: 'status',
        headerName: '',
        width: 50,
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({row}) => {
          const color =
            statusColorMap[row.status] === 'success'
              ? 'success.main'
              : statusColorMap[row.status] === 'warning'
                ? 'warning.main'
                : statusColorMap[row.status] === 'error'
                  ? 'error.main'
                  : 'grey.400';
          return (
            <Tooltip title={row.status}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: color,
                }}
              />
            </Tooltip>
          );
        },
      },
      {
        field: 'dates',
        headerName: 'Access ends',
        width: 170,
        valueGetter: (value, row) => row?.endDate ?? '',
        renderHeader: () => (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="body2" fontWeight={600}>
              Access ends
            </Typography>
            <Tooltip title="When access ends across this product's current and upcoming terms. Open a product to see its individual terms.">
              <InfoOutlinedIcon sx={{fontSize: 16, color: 'text.secondary'}} />
            </Tooltip>
          </Stack>
        ),
        renderCell: ({row}) => (
          <Tooltip title={termEndsTooltip(row)}>
            <Typography variant="body2">{termEndsLabel(row)}</Typography>
          </Tooltip>
        ),
      },
      {
        field: 'quota',
        headerName: 'Unallocated Quota',
        flex: 0.4,
        minWidth: 150,
        sortable: false,
        renderCell: ({row}) => {
          const primary = getEntitlementPrimaryQuota(row);
          if (!primary)
            return (
              <Typography variant="body2" color="text.secondary">
                N/A
              </Typography>
            );
          if (primary.isUnlimited)
            return <Typography variant="body2">Unlimited</Typography>;
          if (primary.limit === null || primary.limit === undefined)
            return (
              <Typography variant="body2" color="text.secondary">
                N/A
              </Typography>
            );
          const remaining = primary.limit - (primary.used ?? 0);
          return (
            <Typography variant="body2">
              {renderQuota(remaining, primary.units)}
            </Typography>
          );
        },
      },
      {
        field: 'opportunityIds',
        headerName: 'Opportunity ID',
        width: 170,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        valueGetter: (value, row) => (row?.plNumbers ?? []).join(', '),
        renderCell: ({row}) => {
          const plNumbers = row?.plNumbers ?? [];
          if (plNumbers.length === 0) {
            return (
              <Tooltip title="No opportunity recorded for this product">
                <Typography variant="body2" color="text.secondary">
                  —
                </Typography>
              </Tooltip>
            );
          }
          if (plNumbers.length === 1) {
            return <Typography variant="body2">{plNumbers[0]}</Typography>;
          }
          const extra = plNumbers.length - 1;
          return (
            <Tooltip title={plNumbers.join(', ')}>
              <Typography variant="body2" noWrap>
                {plNumbers[0]}{' '}
                <Typography component="span" variant="body2" color="primary">
                  +{extra}
                </Typography>
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: 'actions',
        headerName: '',
        width: 130,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({row}) => {
          // Plans are read-only and the whole row navigates to plan detail, so
          // they carry no row-level actions — the doc icon would be redundant
          // with the row click.
          if (row.source === EntitlementSource.PLAN) return null;
          return (
            <Stack direction="row" spacing={0} alignItems="center">
              <Tooltip title="Product History">
                <IconButton
                  size="small"
                  onClick={e => {
                    e.stopPropagation();
                    history.push(
                      routePath(Routes.PRODUCT_DETAIL, {
                        productAccessId: row.sourceId,
                      })
                    );
                  }}
                >
                  <ReceiptLongOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <EntitlementActionsMenu
                entitlement={row}
                onAdjustQuota={handleAdjustQuota}
                onAllocate={showAllocationUI ? handleAllocate : undefined}
                onDeactivate={handleDeactivate}
                onFilterWorkspaces={
                  showAllocationUI ? handleFilterWorkspaces : undefined
                }
                onRecallQuota={handleRecallQuota}
              />
            </Stack>
          );
        },
      },
    ],
    [
      handleAdjustQuota,
      handleAllocate,
      handleDeactivate,
      handleFilterWorkspaces,
      handleRecallQuota,
      history,
      itemTypeNameMap,
      showAllocationUI,
    ]
  );

  if (!isPlanetAdmin) {
    return <ErrorPage statusCode={404} />;
  }

  if (accountQuery.isLoading || accountQuery.isFetching) {
    return <Notifier progress open message="Loading account..." />;
  }

  if (accountQuery.error) {
    return <ErrorPage {...getError(accountQuery.error)} />;
  }

  return (
    <>
      <Header name={account.name} breadcrumbs={breadcrumbs}>
        {isPlanetAdmin && (
          <Stack flexDirection="row" gap={1}>
            {sfdcAccountUrl && (
              <Button
                component="a"
                href={sfdcAccountUrl}
                rel="noopener noreferrer"
                target="_blank"
                variant="outlined"
                size="small"
              >
                Salesforce
              </Button>
            )}
            <Button
              color="primary"
              onClick={() => setModalMode('add-product')}
              size="small"
              variant="outlined"
            >
              Add Product
            </Button>
            {showAllocationUI && (
              <Button
                color="primary"
                onClick={() => setBulkAllocateOpen(true)}
                size="small"
                variant="outlined"
                disabled={caProductAccesses.length === 0}
              >
                Bulk allocate
              </Button>
            )}
            <IconButton
              aria-label="More options"
              size="small"
              onClick={e => setActionsAnchorEl(e.currentTarget)}
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={actionsAnchorEl}
              open={!!actionsAnchorEl}
              onClose={() => setActionsAnchorEl(null)}
            >
              <MenuItem
                onClick={() => {
                  setActionsAnchorEl(null);
                  setEmailDialogOpen(true);
                }}
              >
                Send allocation instructions
              </MenuItem>
            </Menu>
          </Stack>
        )}
      </Header>

      {/* Account metadata */}
      <Box
        display="flex"
        flexWrap="wrap"
        gap={1.5}
        alignItems="center"
        sx={{opacity: 0.7}}
      >
        <Typography variant="caption" color="textSecondary">
          Created {displayDate(account.created_at)}
        </Typography>
        <CopyableId label="Account ID" value={account.id} />
        {account.sfdc_id && <CopyableId label="SFDC" value={account.sfdc_id} />}
        {account.program_id ? (
          <CopyableId
            label="Program"
            value={String(account.program_id)}
            display={account.program_name || programName(account.program_id)}
          />
        ) : null}
      </Box>

      <Divider sx={{my: 3}} />

      {/* Tabs */}
      <Box
        sx={{borderBottom: 1, borderColor: 'divider'}}
        display="flex"
        justifyContent="space-between"
        alignItems="flex-end"
      >
        <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)}>
          <Tab label={`Products${entLoading ? '' : ` (${activeCount})`}`} />
          <Tab
            label={
              imsWorkspaces ? `Workspaces (${workspaces.length})` : 'Workspaces'
            }
          />
        </Tabs>
        {tabIndex === 0 && (
          <Box display="flex" alignItems="center" gap={2} pb={0.75}>
            <TextField
              size="small"
              placeholder="Filter by product or opportunity ID..."
              value={productSearch}
              onChange={e => setProductSearch(e.target.value)}
              sx={{
                width: 220,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'grey.50',
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <Stack direction="row" spacing={1}>
              {PRODUCT_STATUS_FILTERS.map(f => (
                <Chip
                  key={f.value}
                  label={f.label}
                  size="small"
                  onClick={() => {
                    setProductStatus(f.value);
                  }}
                  color={productStatus === f.value ? 'primary' : 'default'}
                  variant={productStatus === f.value ? 'filled' : 'outlined'}
                />
              ))}
            </Stack>
          </Box>
        )}
        {tabIndex === 1 && (
          <Box display="flex" alignItems="center" gap={2} pb={0.75}>
            <Stack direction="row" spacing={1}>
              {STATUS_FILTERS.map(f => (
                <Chip
                  key={f.value}
                  label={f.label}
                  size="small"
                  onClick={() => setWsStatus(f.value)}
                  color={wsStatus === f.value ? 'primary' : 'default'}
                  variant={wsStatus === f.value ? 'filled' : 'outlined'}
                />
              ))}
            </Stack>
          </Box>
        )}
      </Box>

      {/* Products tab */}
      {tabIndex === 0 && (
        <Box mt={1}>
          {entError && (
            <Alert severity="error" sx={{mb: 2}}>
              Couldn&apos;t load products for this account. Try again shortly.
            </Alert>
          )}
          <DataGrid
            columns={entitlementColumns}
            loading={entLoading}
            rows={visibleRows}
            slots={{noRowsOverlay: CustomNoRowsOverlay}}
            autoHeight
            density="standard"
            disableRowSelectionOnClick
            pageSizeOptions={[25]}
            initialState={{
              pagination: {paginationModel: {pageSize: 25}},
            }}
            onRowClick={params => {
              const dest = entitlementRoute(params.row, accountId);
              if (dest) history.push(dest);
            }}
            // PLAN and PRODUCT_ACCESS rows navigate on click; other rows are
            // read-only here. Tag the navigable ones so the pointer cursor +
            // hover affordance only applies to rows that actually do something —
            // otherwise a read-only row looks clickable but isn't.
            getRowClassName={params =>
              entitlementRoute(params.row, accountId)
                ? 'entitlement-row--clickable'
                : 'entitlement-row--readonly'
            }
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnHeaders': {
                borderBottom: `1px solid`,
                borderColor: 'divider',
              },
              '& .MuiDataGrid-row.entitlement-row--clickable': {
                cursor: 'pointer',
                '&:hover': {backgroundColor: 'action.hover'},
              },
              '& .MuiDataGrid-row.entitlement-row--readonly': {
                cursor: 'default',
                '&:hover': {backgroundColor: 'transparent'},
              },
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
                borderBottom: '1px solid',
                borderColor: 'grey.100',
              },
              '& .MuiDataGrid-footerContainer': {
                borderTop: '1px solid',
                borderColor: 'divider',
              },
            }}
          />
        </Box>
      )}

      {/* Workspaces tab */}
      {tabIndex === 1 && (
        <Box mt={2}>
          {workspacesLoading ? (
            <Stack spacing={1}>
              <LinearProgress aria-label="Loading workspaces" />
              {[0, 1].map(i => (
                <Skeleton
                  key={i}
                  variant="rounded"
                  height={72}
                  sx={{borderRadius: 1}}
                />
              ))}
            </Stack>
          ) : workspacesError ? (
            <Alert severity="error">
              Failed to load workspace data. Try refreshing the page.
            </Alert>
          ) : workspaces.length === 0 ? (
            <Alert severity="info">
              No workspace allocations found for this customer account.
            </Alert>
          ) : (
            <Stack spacing={1}>
              {workspaceTruncated && (
                <Alert severity="warning">
                  Showing first 100 workspaces. This account may have more
                  &mdash; use search to narrow results.
                </Alert>
              )}
              {workspacePasTruncated && (
                <Alert severity="warning">
                  Only showing the first 500 workspace allocations. Some
                  products may be missing from the workspace view.
                </Alert>
              )}
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{mb: 1}}
              >
                <Typography variant="body2" color="textSecondary">
                  {productFilter
                    ? `${workspaces.filter(ws => ws.pas.some(pa => pa.edp_id === productFilter)).length} of ${workspaces.length}`
                    : workspaces.length}{' '}
                  workspace{workspaces.length !== 1 ? 's' : ''}. Workspace
                  allocation is managed by the customer.
                </Typography>
                {productFilter && (
                  <Chip
                    label={`Filtered: ${productFilter}`}
                    size="small"
                    onDelete={() => setProductFilter(null)}
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Stack>

              {orphanedPaWorkspaces.length > 0 && (
                <Alert severity="warning" sx={{mb: 1}}>
                  {orphanedPaWorkspaces.length} workspace
                  {orphanedPaWorkspaces.length !== 1 ? 's have' : ' has'}{' '}
                  product allocations but{' '}
                  {orphanedPaWorkspaces.length !== 1 ? 'are' : 'is'} not in the
                  workspace list. The workspace list may be truncated or these
                  workspaces may have been deleted.
                </Alert>
              )}

              {(productFilter
                ? workspaces.filter(ws =>
                    ws.pas.some(pa => pa.edp_id === productFilter)
                  )
                : workspaces
              ).map(ws => (
                <Accordion
                  key={ws.workspaceId}
                  disableGutters
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    '&:before': {display: 'none'},
                    boxShadow: 'none',
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={2}
                      width="100%"
                      pr={2}
                    >
                      <Box flex={1} minWidth={0}>
                        {ws.workspaceId ? (
                          <Tooltip title="View workspace details">
                            <Link
                              component={RouterLink}
                              to={routePath(Routes.WORKSPACE_DETAIL, {
                                accountId,
                                workspaceId: ws.workspaceId,
                              })}
                              // The accordion header toggles on click; stop the
                              // link from also expanding/collapsing it.
                              onClick={e => e.stopPropagation()}
                              variant="body2"
                              fontWeight={600}
                              color="primary.dark"
                              underline="hover"
                              noWrap
                              sx={{display: 'block'}}
                            >
                              {ws.workspaceName}
                            </Link>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {ws.workspaceName}
                          </Typography>
                        )}
                        <Typography variant="caption" color="textSecondary">
                          {ws.pas.length} product
                          {ws.pas.length !== 1 ? 's' : ''}
                          {ws.pas.length > 0 &&
                            !ws.pas.some(isActivePa) &&
                            ' · no active products'}
                        </Typography>
                      </Box>
                      <Stack spacing={0.5}>
                        {Object.entries(ws.quotaByUnit).map(
                          ([unit, {total, used}]) => (
                            <EntitlementQuotaBar
                              key={unit}
                              quota={{
                                used,
                                limit: total,
                                displayUnits: unit === '_none' ? '' : unit,
                                isUnlimited: false,
                              }}
                              compact
                            />
                          )
                        )}
                      </Stack>
                      {ws.workspaceId ? (
                        <CopyIconButton
                          label="Workspace ID"
                          value={String(ws.workspaceId)}
                        />
                      ) : null}
                      <Tooltip title="Open in Insights">
                        <IconButton
                          size="small"
                          component="a"
                          href={`${INSIGHTS_WORKSPACE_URL}/${ws.workspaceId}/info`}
                          target="_blank"
                          rel="noopener"
                          onClick={e => e.stopPropagation()}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Product</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Remaining</TableCell>
                          <TableCell>Term</TableCell>
                          <TableCell />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {ws.pas.map(pa => {
                          const {label: statusLabel, color: statusColor} =
                            paStatus(pa.state);
                          // De-emphasize non-active rows (expired/inactive) with
                          // muted text, no strikethrough. Pending stays normal.
                          const muted = !isActivePa(pa);
                          return (
                            <TableRow
                              key={pa.id}
                              sx={{'&:hover': {bgcolor: 'action.hover'}}}
                            >
                              <TableCell>
                                <Link
                                  component={RouterLink}
                                  to={routePath(Routes.PRODUCT_DETAIL, {
                                    productAccessId: pa.id,
                                  })}
                                  variant="body2"
                                  fontWeight={muted ? 500 : 600}
                                  color={
                                    muted ? 'text.secondary' : 'primary.dark'
                                  }
                                  sx={{
                                    // Muted rows drop the color cue, so the
                                    // underline carries the link affordance.
                                    textDecoration: muted
                                      ? 'underline'
                                      : 'none',
                                    textDecorationStyle: muted
                                      ? 'dotted'
                                      : undefined,
                                    '&:hover': {textDecoration: 'underline'},
                                  }}
                                >
                                  {pa.title || pa.name}
                                </Link>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={statusLabel}
                                  size="small"
                                  variant="outlined"
                                  color={statusColor}
                                  sx={{
                                    height: 20,
                                    fontSize: '0.65rem',
                                    ...(statusColor
                                      ? {}
                                      : {
                                          color: 'text.secondary',
                                          borderColor: 'divider',
                                        }),
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  color={muted ? 'text.secondary' : undefined}
                                >
                                  {pa.unlimited_quota
                                    ? 'Unlimited'
                                    : pa.quota_total === null ||
                                        pa.quota_total === undefined
                                      ? 'N/A'
                                      : renderQuota(
                                          pa.quota_total - (pa.quota_used ?? 0),
                                          pa.quota_units
                                        )}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  color={muted ? 'text.secondary' : undefined}
                                >
                                  {formatDate(pa.term_start_date)} —{' '}
                                  {formatDate(pa.term_end_date)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {showAllocationUI && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={e => {
                                      e.stopPropagation();
                                      setWsAllocTarget(pa);
                                    }}
                                  >
                                    {(pa.quota_total === null ||
                                      pa.quota_total === undefined) &&
                                    !pa.unlimited_quota
                                      ? 'Move'
                                      : 'Allocate'}
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          )}
        </Box>
      )}

      <QuotaAdjustModal
        open={!!adjustTarget}
        onClose={() => setAdjustTarget(null)}
        entitlement={adjustTarget}
      />

      {recallTarget && (
        <RecallWorkspacesModal
          open={!!recallTarget}
          onClose={() => setRecallTarget(null)}
          onSuccess={() => {
            queryClient.invalidateQueries(['caWorkspacePas', accountId]);
            queryClient.invalidateQueries(['productAccesses']);
          }}
          workspacePas={(workspacePas || []).filter(
            pa => pa.edp_id === recallTarget.rawPayload?.edp_id
          )}
          workspacePasLoading={!workspacePasQuery.data}
          workspacePasTruncated={workspacePasTruncated}
        />
      )}

      {showAllocationUI && allocateTarget && (
        <AllocationDialog
          accountId={accountId}
          open={!!allocateTarget}
          onClose={() => setAllocateTarget(null)}
          productAccess={allocateTarget}
        />
      )}

      {showAllocationUI && wsAllocTarget && (
        <AllocationDialog
          accountId={accountId}
          open={!!wsAllocTarget}
          onClose={() => setWsAllocTarget(null)}
          productAccess={wsAllocTarget}
          requireWorkspace={false}
          sourceWorkspaceId={wsAllocTarget.workspace_id}
        />
      )}

      {showAllocationUI && bulkAllocateOpen && (
        <BulkAllocationDialog
          accountId={accountId}
          open={bulkAllocateOpen}
          onClose={() => setBulkAllocateOpen(false)}
          productAccesses={caProductAccesses}
        />
      )}

      {isPlanetAdmin && (
        <AddProductToAccountModal
          open={!!modalMode}
          onClose={() => setModalMode(null)}
          customerAccountId={accountId}
          mode={modalMode ?? 'add-product'}
          productAccess={null}
        />
      )}
      {isPlanetAdmin && (
        <AllocationInstructionsEmailDialog
          open={emailDialogOpen}
          onClose={() => setEmailDialogOpen(false)}
          customerAccountId={accountId}
        />
      )}
      <Dialog
        open={!!deactivateTarget}
        onClose={() => {
          if (!isDeactivating) {
            setDeactivateTarget(null);
            setDeactivateError(null);
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Deactivate {deactivateTarget?.name}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently deactivate this product. The customer will
            immediately lose access. This cannot be undone without
            reprovisioning.
          </DialogContentText>
          {deactivateError && (
            <DialogContentText color="error" sx={{mt: 1}}>
              {deactivateError}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeactivateTarget(null)}
            disabled={isDeactivating}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={() => doDeactivate()}
            disabled={isDeactivating}
            color="error"
            variant="contained"
          >
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NewAccountDetailPage;
